import { memo, useCallback } from 'react';
import { Menu } from '@mantine/core';

import { useMapOverlayMenu } from '@/hooks';
import { useWorkspace } from '@/hooks/useWorkspace';
import { Z_INDEX_DROPDOWN } from '@/constants/zIndex';
import { WorkspaceSelectorTarget } from './WorkspaceSelectorTarget';
import { WorkspaceSelectorContent } from './WorkspaceSelectorContent';
import type { WorkspaceSelectorProps } from './WorkspaceSelector.types';
import styles from './WorkspaceSelector.module.css';

const MENU_OFFSET = 4;
const MENU_TRANSITION_DURATION = 150;

export const WorkspaceSelector = memo(function WorkspaceSelector({
  onWorkspaceSwitch,
  fullWidth,
  className,
  withinPortal = true,
  onAfterAction,
}: WorkspaceSelectorProps) {
  const { currentWorkspace } = useWorkspace();

  const [isOpen, setIsOpen] = useMapOverlayMenu();

  const handleAfterAction = useCallback(() => {
    setIsOpen(false);
    onAfterAction?.();
  }, [onAfterAction, setIsOpen]);

  return (
    <Menu
      opened={isOpen}
      onChange={setIsOpen}
      shadow="md"
      width="target"
      position="bottom-start"
      offset={MENU_OFFSET}
      zIndex={Z_INDEX_DROPDOWN}
      withinPortal={withinPortal}
      transitionProps={{
        duration: MENU_TRANSITION_DURATION,
        transition: 'fade',
      }}
      classNames={{ dropdown: styles.dropdown }}
    >
      <Menu.Target>
        <WorkspaceSelectorTarget
          workspace={currentWorkspace}
          isOpen={isOpen}
          fullWidth={fullWidth}
          className={className}
        />
      </Menu.Target>

      <Menu.Dropdown>
        <WorkspaceSelectorContent
          onWorkspaceSwitch={onWorkspaceSwitch}
          onAfterAction={handleAfterAction}
        />
      </Menu.Dropdown>
    </Menu>
  );
});
